import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import type { AssetEntry, LoadProgress } from '../types';
import { normalizeOrigin, type NormalizeParams } from '../utils/modelUtils';

export class AssetLoader {
  private loader: GLTFLoader;
  private cache = new Map<string, GLTF>();
  private loading = new Map<string, Promise<GLTF>>();
  private normalizeCache = new Map<string, NormalizeParams>();
  private dimensionsCache = new Map<string, { x: number; y: number; z: number }>();
  onProgress?: (progress: LoadProgress) => void;

  constructor() {
    this.loader = new GLTFLoader();
    // DRACO for compressed GLB files
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    this.loader.setDRACOLoader(draco);
  }

  async load(entry: AssetEntry): Promise<GLTF> {
    if (this.cache.has(entry.key)) {
      return this.cache.get(entry.key)!;
    }

    if (this.loading.has(entry.key)) {
      return this.loading.get(entry.key)!;
    }

    const promise = new Promise<GLTF>((resolve, reject) => {
      this.loader.load(
        entry.path,
        (gltf) => {
          this.cache.set(entry.key, gltf);
          this.loading.delete(entry.key);
          resolve(gltf);
        },
        undefined,
        (error) => {
          this.loading.delete(entry.key);
          console.warn(`Failed to load ${entry.key}: ${entry.path}`, error);
          reject(error);
        }
      );
    });

    this.loading.set(entry.key, promise);
    return promise;
  }

  async loadPhase(entries: AssetEntry[], phaseNum: number): Promise<void> {
    let loaded = 0;
    const total = entries.length;
    const BATCH_SIZE = 10; // Slightly above browser's 6-connection limit for pipelining

    for (let i = 0; i < entries.length; i += BATCH_SIZE) {
      const batch = entries.slice(i, i + BATCH_SIZE);
      const promises = batch.map(async (entry) => {
        try {
          await this.load(entry);
        } catch {
          // Warn but don't fail entire phase
        }
        loaded++;
        this.onProgress?.({
          loaded,
          total,
          phase: phaseNum,
          currentAsset: entry.key,
        });
      });

      await Promise.all(promises);
    }
  }

  /**
   * Clone a model with normalized origin (XZ centered, Y grounded).
   * @param fitMax — max XZ size in Kenney units. Models larger than this
   *   get scaled down to fit. Use 1.0 for roads (edge-to-edge tiling),
   *   ~0.9 for buildings (leaves sidewalk margin). Omit for details.
   */
  getModel(key: string, fitMax?: number): THREE.Group | null {
    const gltf = this.cache.get(key);
    if (!gltf) return null;
    const clone = gltf.scene.clone();

    const cacheKey = fitMax != null ? `${key}:${fitMax}` : key;
    if (!this.normalizeCache.has(cacheKey)) {
      const box = new THREE.Box3().setFromObject(clone);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxXZ = Math.max(size.x, size.z);
      // Only shrink oversized models, never enlarge small ones
      const fitScale = fitMax != null && maxXZ > fitMax ? fitMax / maxXZ : 1;

      // wrapper transform: Translate * Scale, so
      // finalPos = position + scale * childPos
      // To center: position = -scale * center
      this.normalizeCache.set(cacheKey, {
        x: -fitScale * center.x,
        y: -fitScale * box.min.y,
        z: -fitScale * center.z,
        fitScale,
      });
    }

    normalizeOrigin(clone, this.normalizeCache.get(cacheKey)!);
    return clone;
  }

  getOriginal(key: string): GLTF | null {
    return this.cache.get(key) ?? null;
  }

  // Fit-scaled dimensions for one reference clone, cached per (key, fitMax).
  // Skips per-instance Box3.setFromObject in hot placement loops.
  getModelDimensions(key: string, fitMax?: number): { x: number; y: number; z: number } | null {
    const cacheKey = fitMax != null ? `${key}:${fitMax}` : key;
    const cached = this.dimensionsCache.get(cacheKey);
    if (cached) return cached;

    const gltf = this.cache.get(key);
    if (!gltf) return null;

    const clone = gltf.scene.clone();
    const box = new THREE.Box3().setFromObject(clone);
    const size = box.getSize(new THREE.Vector3());
    const maxXZ = Math.max(size.x, size.z);
    const fitScale = fitMax != null && maxXZ > fitMax ? fitMax / maxXZ : 1;

    const dims = { x: size.x * fitScale, y: size.y * fitScale, z: size.z * fitScale };
    this.dimensionsCache.set(cacheKey, dims);
    return dims;
  }

  getAnimations(key: string): THREE.AnimationClip[] {
    const gltf = this.cache.get(key);
    return gltf?.animations ?? [];
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }

  dispose() {
    this.cache.clear();
    this.loading.clear();
    this.normalizeCache.clear();
    this.dimensionsCache.clear();
  }
}
